// src/components/WorldMap.tsx
import React, { useMemo, useEffect, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { feature } from "topojson-client";
import worldTopo from "world-atlas/countries-110m.json";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getColoredCarSvg } from "./carIcon";

const COLORS = ["#1f77b4", "#ff7f0e", "#A3C1AD", "#d62728", "#9467bd"];

const svgToBase64 = (svg: string) =>
  "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));

export default function WorldMap({ highlightId }: { highlightId?: string }) {

  const trips = useSelector((s: RootState) => s.fleet.trips);
  const pointer = useSelector((s: RootState) => s.fleet.pointer);
  const now = useSelector((s: RootState) => s.fleet.now);
  const start = useSelector((s: RootState) => s.fleet.start);

  const isReset =
    pointer === 0 && Object.keys(trips).length === 0 && now === start;

  const [mapReady, setMapReady] = useState(false);
  const echartsRef = useRef<any>(null);

  const worldGeoJson = useMemo(
    () => feature(worldTopo as any, (worldTopo as any).objects.countries),
    []
  );

  useEffect(() => {
    try {
      echarts.registerMap("world", worldGeoJson as any);
      setMapReady(true);
    } catch (e) {
      console.error("registerMap failed", e);
      setMapReady(false);
    }
  }, [worldGeoJson]);

  // Get all trips with a started flag and valid vehicle
  const tripEntries = useMemo(
    () =>
      Object.entries(trips || {})
        .filter(([_, t]: any) => t?.started && t?.vehicleId)
        .slice(0, 5),
    [trips]
  );

  // color mapping
  const tripColorMap = useMemo(() => {
    const m: Record<string, string> = {};
    tripEntries.forEach(([id], idx) => {
      m[id] = COLORS[idx % COLORS.length];
    });
    return m;
  }, [tripEntries]);

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance?.();
    if (!chart) return;
    if (isReset) chart.clear();
  }, [isReset]);

  const encodedIcons = useMemo(() => {
    return Object.fromEntries(
      tripEntries.map(([id], idx) => {
        const color = COLORS[idx % COLORS.length];
        const svg = getColoredCarSvg(highlightId === id ? "#000000" : color);
        return [id, svgToBase64(svg)];
      })
    );
  }, [tripEntries,highlightId]);

  // series now uses vehicleId for label + data name
  const series = useMemo(() => {
  const out: any[] = [];

  for (const [id, trip] of tripEntries) {
    if (!trip) continue;
    const color = tripColorMap[id] || "#888";
    const icon = encodedIcons[id];

    // ---- ROUTE LINE ----
    const validPath =
      Array.isArray(trip.path) && trip.path.length > 1
        ? trip.path.filter(
            ([lat, lon]: [number, number]) =>
              Number.isFinite(lat) && Number.isFinite(lon)
          )
        : [];

    if (validPath.length > 1) {

for (const [id, trip] of tripEntries) {
  const color = tripColorMap[id] || "#888";
  const icon = encodedIcons[id];
  if (!trip || !trip.last) continue;

  const lat = Number(trip.last.lat);
  const lon = Number(trip.last.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

  const vehicleLabel = trip.vehicleId ?? id;   

  out.push({
    // id,
    name: vehicleLabel, // ✅ now defined
    type: "scatter",
    coordinateSystem: "geo",
    animation: false,
    symbol: `image://${icon}`,
    symbolKeepAspect: true,
    symbolSize:32,
    data: [
      {
        name: vehicleLabel,
        value: [lon, lat],
        tripId: id,
        status: trip.status ?? "unknown",
      },
    ],
    itemStyle: {
      color,
    },
  
  });
}
    }

    // ---- VEHICLE ICON ----
    if (
      icon &&
      trip.last &&
      Number.isFinite(trip.last.lat) &&
      Number.isFinite(trip.last.lon)
    ) {
      const { lat, lon } = trip.last;
      const vehicleLabel = trip.vehicleId ?? id;
      out.push({
        id,
        name: vehicleLabel,
        type: "scatter",
        coordinateSystem: "geo",
        symbol: `image://${icon}`,
        symbolSize: 30,
        symbolKeepAspect: true,
        data: [{ name: vehicleLabel, value: [lon, lat], tripId: id }],
        itemStyle: { color },
        zlevel: 2,
      });
    }
  }

  return out;
}, [tripEntries, encodedIcons, tripColorMap]);

 
  const option = useMemo(() => {
    return {
      backgroundColor: "#ffffff",
      animation: false,
      tooltip: {
  trigger: "item",
  backgroundColor: "#111827",
  borderColor: "#1f2937",
  textStyle: { color: "#fff", fontSize: 12 },
  padding: 8,
  formatter: (params: any) => {
    const trip = trips[params.data?.tripId];
    if (!trip) return params.name;

    const status = (trip.status || "ACTIVE").toString().toUpperCase();
    const progress = Math.round(trip.progress ?? 0);
    const tripId = params.data?.tripId ?? "N/A";
    const fuel = typeof trip.fuel === "number" ? `${Math.round(trip.fuel)}%` : "—";
    const speed = typeof trip.speedKph === "number" ? `${trip.speedKph} km/h` : "—";

    return `
      <div style="display:flex;flex-direction:column;gap:2px;min-width:120px;">
        <div style="font-weight:600;margin-bottom:2px;">${params.name}</div>
        <strong>Trip ID:</strong> ${tripId}<br/>
        <div><b>Status:</b> ${status}</div>
        <div><b>Progress:</b> ${progress}%</div>
        <div><b>Speed:</b> ${speed}</div>
        <div><b>Fuel:</b> ${fuel}</div>
        
      </div>
    `;
  },
},

      geo: {
        map: "world",
        roam: "move",
        silent: true,
        label: { show: false },
        itemStyle: { areaColor: "#f7f7f7", borderColor: "#ddd" },
        emphasis: { itemStyle: { areaColor: "#eaeaea" } },
        center: [-95, 37],
        zoom: 6,
      },
      series,
    };
  }, [series,trips]);

  const tripLegend = useMemo(() => {
    return tripEntries.map(([id, t]) => ({
      id,
      vehicleId: t.vehicleId ?? id,
    }));
  }, [tripEntries]);

  if (!mapReady) {
    return <div className="h-[480px] m-3">Loading map…</div>;
  }

  return (
    <div className="relative h-[480px] m-3">
      <ReactECharts
        ref={echartsRef}
        className="h-full w-full"
        style={{ height: "100%" }}
        option={option}
        notMerge={true}
        lazyUpdate
      />

      {/* Legend: bottom of map */}
      {tripLegend.length === 0 ? null : (
        <div className="absolute bottom-3 left-3 w-min">
          <div className="bg-[#eff4ff] rounded-xl px-3 py-2">
            <div className="flex flex-wrap items-center gap-4">
              {tripLegend.map(({ id, vehicleId }) => (
                <div key={id} className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-md"
                    style={{ background: tripColorMap[id] }}
                  />
                  <span className="text-sm font-medium">{vehicleId}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
